import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiPhone, FiSave, FiBell, FiShield, FiSmartphone, FiTrash2, FiPlus } from 'react-icons/fi';
import './Profile.css';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    preferences: user?.preferences || {
      smsNotifications: true,
      whatsappNotifications: true,
      autoGenerateGatePass: true,
      autoShareInstructions: true
    }
  });

  // Check if WebAuthn is supported
  const isWebAuthnSupported = () => {
    return window.PublicKeyCredential !== undefined;
  };

  // Fetch user's passkeys on mount
  useEffect(() => {
    const fetchAuthMethods = async () => {
      try {
        const response = await authAPI.getAuthMethods(user?.phone?.replace('+91', ''));
        if (response.data.hasPasskeys) {
          setPasskeys(Array(response.data.passkeyCount).fill({ deviceName: 'Registered Device' }));
        }
      } catch (error) {
        console.error('Failed to fetch auth methods:', error);
      }
    };
    if (user?.phone) {
      fetchAuthMethods();
    }
  }, [user?.phone]);

  // Register a new passkey
  const handleAddPasskey = async () => {
    if (!isWebAuthnSupported()) {
      toast.error('Passkey/biometric login is not supported on this device');
      return;
    }

    setPasskeyLoading(true);
    try {
      // Get challenge from server
      const challengeResponse = await authAPI.getPasskeyRegisterChallenge();
      const { challenge, rpId, rpName, userId, userName, userDisplayName } = challengeResponse.data;

      // Create credential options
      const publicKeyCredentialCreationOptions = {
        challenge: Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        rp: {
          name: rpName,
          id: rpId
        },
        user: {
          id: Uint8Array.from(userId, c => c.charCodeAt(0)),
          name: userName,
          displayName: userDisplayName
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },  // ES256
          { alg: -257, type: 'public-key' } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'preferred'
        },
        timeout: 60000,
        attestation: 'none'
      };

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      // Send credential to server
      const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

      // Use attestationObject as the public key data (more compatible)
      const attestationObject = btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

      await authAPI.registerPasskey({
        credentialId,
        publicKey: attestationObject,
        deviceName: navigator.userAgent.includes('Mobile') ? 'Mobile Device' : 'Desktop Device'
      });

      toast.success('Passkey added successfully! You can now use biometric login.');
      setPasskeys(prev => [...prev, { deviceName: navigator.platform || 'Unknown Device' }]);
    } catch (error) {
      console.error('Passkey registration error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Passkey is not supported on this device');
      } else {
        toast.error(error.response?.data?.message || 'Failed to add passkey');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('pref_')) {
      const prefName = name.replace('pref_', '');
      setFormData(prev => ({
        ...prev,
        preferences: { ...prev.preferences, [prefName]: checked }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.updateProfile(formData);
      updateUser(response.data.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="container">
        <h1>My Profile</h1>

        <form onSubmit={handleSubmit} className="profile-form card">
          <div className="form-section">
            <h2><FiUser /> Personal Information</h2>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="name"
                className="form-input"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <div className="phone-display">
                <FiPhone />
                <span>{user?.phone}</span>
                <span className="verified-badge">Verified</span>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2><FiBell /> Notification Preferences</h2>

            <div className="preferences-grid">
              <label className="pref-item">
                <input
                  type="checkbox"
                  name="pref_smsNotifications"
                  checked={formData.preferences.smsNotifications}
                  onChange={handleChange}
                />
                <div>
                  <span className="pref-title">SMS Notifications</span>
                  <span className="pref-desc">Receive order updates via SMS</span>
                </div>
              </label>

              <label className="pref-item">
                <input
                  type="checkbox"
                  name="pref_whatsappNotifications"
                  checked={formData.preferences.whatsappNotifications}
                  onChange={handleChange}
                />
                <div>
                  <span className="pref-title">WhatsApp Notifications</span>
                  <span className="pref-desc">Receive updates on WhatsApp</span>
                </div>
              </label>

              <label className="pref-item">
                <input
                  type="checkbox"
                  name="pref_autoGenerateGatePass"
                  checked={formData.preferences.autoGenerateGatePass}
                  onChange={handleChange}
                />
                <div>
                  <span className="pref-title">Auto-generate Gate Pass</span>
                  <span className="pref-desc">Automatically create entry passes for orders</span>
                </div>
              </label>

              <label className="pref-item">
                <input
                  type="checkbox"
                  name="pref_autoShareInstructions"
                  checked={formData.preferences.autoShareInstructions}
                  onChange={handleChange}
                />
                <div>
                  <span className="pref-title">Auto-share Instructions</span>
                  <span className="pref-desc">Share delivery instructions with riders automatically</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiSave />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Security Section - Outside the form */}
        <div className="profile-form card">
          <div className="form-section">
            <h2><FiShield /> Security</h2>

            <div className="security-item">
              <div className="security-header">
                <div>
                  <h3><FiSmartphone /> Passkey / Biometric Login</h3>
                  <p className="security-desc">
                    Use fingerprint, face recognition, or device PIN for quick and secure login
                  </p>
                </div>
              </div>

              {!isWebAuthnSupported() ? (
                <div className="passkey-not-supported">
                  <p>Passkey is not supported on this browser/device</p>
                </div>
              ) : (
                <>
                  {passkeys.length > 0 ? (
                    <div className="passkeys-list">
                      {passkeys.map((passkey, index) => (
                        <div key={index} className="passkey-item">
                          <div className="passkey-info">
                            <FiSmartphone />
                            <span>{passkey.deviceName || 'Device ' + (index + 1)}</span>
                          </div>
                          <span className="passkey-status active">Active</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-passkeys">No passkeys registered yet</p>
                  )}

                  <button
                    type="button"
                    className="btn btn-secondary add-passkey-btn"
                    onClick={handleAddPasskey}
                    disabled={passkeyLoading}
                  >
                    <FiPlus />
                    {passkeyLoading ? 'Setting up...' : 'Add Passkey'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
